import React, { useState, Fragment } from "react";
import { Collapse, Glyphicon, Panel, ToggleButtonGroup, ToggleButton, } from "react-bootstrap";
import { SafeHTMLDisplay } from "./SafeHTMLDisplay";
import { StatusLabel } from "./StatusLabel";
import { ColorOptions } from "./types";
import { useTranslation } from "./hooks/useTranslation";
import { Button, AlertModal, Row, Col } from ".";
var ObjectiveTypes;
(function (ObjectiveTypes) {
    ObjectiveTypes["LearningPathObjective"] = "Learning Path";
    ObjectiveTypes["CourseObjective"] = "Course";
    ObjectiveTypes["EventObjective"] = "Event";
})(ObjectiveTypes || (ObjectiveTypes = {}));
export const LearningObjective = ({ title, withLinkProps, description, children, status, type, disabled, start, onClick, onDelete, setComplete, information, id, }) => {
    let isDescriptionShown = true;
    const [showDescription, setShowDescription] = useState(false);
    const [isOpen, setOpen] = React.useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
    const dateFormatOptions = {
        month: "short",
        day: "numeric",
        year: "numeric",
    };
    title = start
        ? `${title} (${start.toLocaleDateString("en-US", dateFormatOptions)})`
        : title;
    const toggleChildObjectives = React.useCallback((ev) => {
        setOpen(!isOpen);
        ev.stopPropagation();
        ev.preventDefault();
    }, [setOpen, isOpen]);
    const panelStyle = children && !isOpen
        ? {
            marginBottom: -1,
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
        }
        : {};
    if (onClick || disabled) {
        panelStyle.cursor = disabled ? "not-allowed" : "pointer";
    }
    const footerStyle = children && !isOpen
        ? { borderTopLeftRadius: 0, borderTopRightRadius: 0 }
        : {};
    const isComplete = status === "completed";
    const t = useTranslation();
    const builtCompleteToggle = React.useMemo(() => {
        const completeClass = `${isComplete ? "btn-success" : ""}`;
        const incompleteClass = `${!isComplete ? "btn-danger" : ""}`;
        return (React.createElement(Col, { xs: 4 },
            React.createElement(ToggleButtonGroup, { name: t("Toggle Status"), className: "pull-right", type: "radio", value: status, onChange: setComplete ? () => setComplete(!isComplete) : () => null, onClick: (e) => e.stopPropagation() },
                React.createElement(ToggleButton, { className: completeClass, value: "completed", disabled: !!disabled, id: id && `${id}__toggle-status--complete` }, "Complete"),
                React.createElement(ToggleButton, { className: incompleteClass, value: "incomplete", disabled: !!disabled, id: id && `${id}__toggle-status--incomplete` }, "Incomplete"))));
    }, [setComplete, isComplete, disabled, status, t, id]);
    if (type === ObjectiveTypes.EventObjective ||
        type === ObjectiveTypes.CourseObjective ||
        type === ObjectiveTypes.LearningPathObjective) {
        isDescriptionShown = false;
    }
    let objective = (React.createElement(React.Fragment, null,
        React.createElement(AlertModal, { type: "warning", show: deleteModalOpen, title: t("Are you sure?"), onDone: (confirmed) => {
                confirmed && onDelete && onDelete();
                setDeleteModalOpen(false);
            }, id: id && `${id}__delete-modal` },
            "Objective '",
            title,
            "' will be removed."),
        React.createElement(Panel, { style: panelStyle, className: disabled ? "disabled" : "", onClick: disabled ? undefined : onClick, id: id },
            title ? (React.createElement(Panel.Heading, null,
                React.createElement(Row, null,
                    React.createElement(Col, { xs: 8 },
                        type ? (React.createElement("span", { style: {
                                padding: "0 0 4px 0",
                                fontWeight: 500,
                                display: "block",
                            } }, type)) : null,
                        React.createElement(Panel.Title, { componentClass: "h3" }, title),
                        information && (React.createElement("div", { className: "learning-objective--information" }, information))),
                    setComplete ? (builtCompleteToggle) : (React.createElement(Col, { xs: 4 },
                        disabled ? (React.createElement("div", { className: "pull-right" },
                            React.createElement(Glyphicon, { glyph: "lock" }),
                            " ",
                            typeof disabled === "string" ? disabled : "Locked")) : null,
                        status === "completed" && !disabled ? (React.createElement("div", { className: "pull-right" },
                            React.createElement(StatusLabel, { color: ColorOptions.Green, text: t("Completed") }))) : null,
                        status === "incomplete" && !disabled ? (React.createElement("div", { className: "pull-right" },
                            React.createElement(StatusLabel, { color: ColorOptions.LightGrey, text: t("Incomplete") }))) : null)),
                    onDelete && (React.createElement(Col, { xs: 4 },
                        React.createElement("div", { className: "learning-objective--deleteButton" },
                            React.createElement(Button, { onClick: (e) => {
                                    setDeleteModalOpen(true);
                                    e.stopPropagation();
                                }, disabled: !!disabled, label: t("Delete"), type: "suppressed", id: id && `${id}__delete-action` }))))))) : null,
            description && !isDescriptionShown && (React.createElement(Fragment, null,
                React.createElement("div", { className: "learning-objective--descriptionButton" },
                    React.createElement(Button, { label: `${showDescription ? "Hide" : "Show"} Description`, onClick: () => setShowDescription(!showDescription), type: "suppressed", icon: showDescription ? "chevron-up" : "chevron-down", id: id && `${id}__show-description` })),
                React.createElement(Collapse, { in: showDescription },
                    React.createElement(Panel.Body, null,
                        React.createElement(SafeHTMLDisplay, { html: description }))))),
            description && isDescriptionShown && (React.createElement(Panel.Body, null,
                React.createElement(SafeHTMLDisplay, { html: description })))),
        children ? (React.createElement("div", null,
            React.createElement(Collapse, { in: isOpen },
                React.createElement("div", null,
                    React.createElement("ul", { className: "learning-path-ul" }, children))),
            React.createElement("div", { onClick: toggleChildObjectives, style: { cursor: "pointer" } },
                React.createElement(Panel, { style: footerStyle, className: "learning-objective--footer" },
                    React.createElement(Panel.Body, { style: {
                            paddingTop: 8,
                            paddingBottom: 8,
                        }, className: "panel-footer" },
                        React.createElement("div", { className: "pull-right" },
                            React.createElement(React.Fragment, null,
                                `${isOpen ? "Hide" : "Show"} Objectives`,
                                " ",
                                React.createElement(Glyphicon, { glyph: `chevron-${isOpen ? "up" : "down"}`, style: { top: 2 } })))))))) : null));
    if (withLinkProps) {
        if (withLinkProps.routerLink) {
            const LinkObjective = withLinkProps.routerLink;
            objective = (React.createElement(LinkObjective, { to: withLinkProps.url, href: withLinkProps.url }, objective));
        }
        else {
            objective = React.createElement("a", { href: withLinkProps.url }, objective);
        }
    }
    return React.createElement("li", { className: "learning-path-li" }, objective);
};
